/** Lite application state. Everything the server can store is kept in Workspace API DTO shape (see dto.ts). */
import type { ComponentInput, FilterModel, VisualizationModel } from './dto';

export type EntrySourceKind = 'file' | 'url' | 'rcsb';

export interface EntrySource {
  kind: EntrySourceKind;
  /** File name with extension as it will be uploaded to the Workspace API. */
  fileName: string;
  /** Lower-case extension without the dot, after stripping .gz (pdb, cif, bcif, sdf, mol, mol2, xyz, gro, pdbqt, pqr). */
  ext: string;
  /** Original bytes, kept for Open in Nanome (the server re-parses them). */
  bytes: Uint8Array;
  gz: boolean;
  /** RCSB id or URL when applicable. */
  ref?: string;
}

export interface Entry {
  /** 1..N, assigned in load order, never reused (matches server semantics). */
  serial: number;
  name: string;
  source: EntrySource;
  /** Number of models/frames in the file (multi-model PDB, multi-record SDF, mmCIF models). */
  modelCount: number;
  /** 1-based, like the server's entryModelSerial. */
  currentModelSerial: number;
  /** Unique chain names in first-appearance order (drives the chain-instance palette index). */
  chains: string[];
  containsProtein: boolean;
  containsNucleic: boolean;
  containsLigands: boolean;
  atomCount: number;
}

export interface Component extends ComponentInput {
  /** Scene-local serial, 1.., never reused. */
  serial: number;
  hidden: boolean;
  /** True while the component is exactly what default-components produced for its entry; Open in Nanome can then use add-default. */
  isDefault: boolean;
  /** Convenience: entry serials referenced by the filters (including nested ones). */
  entrySerials: number[];
}

export type Tool = 'default' | 'selection';
export type SelectionMode = 'atom' | 'residue' | 'chain';

export interface Selection {
  /** entrySerial -> sorted atom indices (0-based, into the entry's current model). */
  byEntry: Record<number, number[]>;
}

export interface ViewState {
  skyboxTheme: string;
  nightMode: boolean;
  tool: Tool;
  selectionMode: SelectionMode;
}

export interface LiteState {
  entries: Entry[];
  components: Component[];
  selection: Selection;
  view: ViewState;
}

export function filterEntrySerials(filters: FilterModel[]): number[] {
  const out = new Set<number>();
  const visit = (fs: FilterModel[]) => {
    for (const f of fs) {
      if (f.kind === 'EntrySerial' && f.entrySerial !== undefined) out.add(f.entrySerial);
      if (f.containedFilters) visit(f.containedFilters);
    }
  };
  visit(filters);
  return [...out].sort((a, b) => a - b);
}

export type { ComponentInput, FilterModel, VisualizationModel };
